import { typeValidator } from '@chilipiper/utils'
import React, { useCallback, useEffect, useId } from 'react'
import { Selection } from 'react-stately'
import { Key } from 'react-aria'
import styled from 'styled-components'
import invariant from 'tiny-invariant'
import { Box, Flex } from '../../../core-components'
import { useSubmenuContext } from '../../action/action-list/SubmenuContext'
import { SettingLabel, SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import {
  CascadeSelectItem,
  SelectEndContent,
  SelectItem,
  SelectSection,
  SelectStartContent,
} from '../select/PublicComponents'
import { SelectItemPlaceholder } from '../select-item/SelectItemPlaceholder'
import { BaseSelectCascade } from '../base-select/BaseSelectCascade'
import { CascadeListFocusProvider } from '../base-select/CascadeListFocusContext'
import { CascadeItems } from '../base-select/CascadeItemSelectList'
import { SelectItemDataProvider } from '../base-select/SelectItemContext'
import { SelectListBase } from './SelectListBase'
import { useSelectListState } from './useSelectListState'

type Props = {
  INTERNAL_keepMaxWidth?: boolean
  INTERNAL_variant?: 'regular' | 'nested'
  children: React.ReactNode
  autofocus?: boolean
  isLoading?: boolean
  useVirtualScroll?: boolean
} & (
  | {
      onChange?: (value?: string, path?: readonly string[]) => void
      value?: string
      valuePath?: readonly string[]
      variant?: 'single'
      triggerWidth?: number
    }
  | {
      onChange?: (value?: string[]) => void
      value?: readonly string[]
      valuePath?: never
      variant: 'multi'
      triggerWidth?: never
    }
) &
  (
    | {
        'aria-label': string
        'aria-labelledby'?: never
        label?: never
      }
    | {
        'aria-label'?: never
        'aria-labelledby': string
        label?: never
      }
    | {
        'aria-label'?: never
        'aria-labelledby'?: never
        label: React.ReactNode
      }
  ) &
  (
    | {
        hasSearch: true
        onSearchChange: (value: string) => void
        search: string
        shouldFilterOnSearch?: boolean
      }
    | { hasSearch?: false; onSearchChange?: never; search?: never; shouldFilterOnSearch?: never }
  )
const SelectListInternal = ({
  children,
  hasSearch,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  onSearchChange,
  search,
  INTERNAL_variant,
  INTERNAL_keepMaxWidth = false,
  autofocus = false,
  label,
  isLoading,
  shouldFilterOnSearch = true,
  useVirtualScroll,
  triggerWidth,
  ...props
}: Props) => {
  const onSelectionChange = (selection: Selection) => {
    if (props.variant === 'multi') {
      if (selection === 'all') {
        return props.onChange?.([...listData.existingValues.values()])
      }
      return props.onChange?.([...selection.values()].map(k => String(k)))
    }
    if (selection === 'all') {
      return props.onChange?.(listData.existingValues.values().next().value)
    }
    return props.onChange?.(
      selection.values().next().value ? String(selection.values().next().value) : undefined
    )
  }

  const {
    state,
    listData,
    keyboardDelegate,
    selectedCascadeItem,
    selectedKeys,
    setSelectedCascadeItem,
    onChange: onChangeHandler,
  } = useSelectListState({
    children,
    onSelectCascadeItem: () => {
      onSearchChange?.('')
    },
    onSelectionChange,
    ...(props.variant === 'multi'
      ? {
          variant: 'multi',
          value: props.value,
          onChange: props.onChange,
        }
      : {
          variant: 'single',
          value: props.value,
          valuePath: props.valuePath,
          onChange: props.onChange,
        }),
    search: shouldFilterOnSearch ? search : undefined,
  })

  const onFocusSelectListItem = useCallback(
    (key: Key) => {
      state.selectionManager.setFocused(true)
      state.selectionManager.setFocusedKey(key)
    },
    [state]
  )
  useEffect(() => {
    if (state.selectionManager.isFocused || !autofocus) {
      return
    }
    const itemToFocus = selectedKeys[0] ?? state.collection.getFirstKey()
    if (itemToFocus) {
      onFocusSelectListItem(itemToFocus)
    }
  }, [selectedKeys, onFocusSelectListItem, state, autofocus])
  const submenuProps = useSubmenuContext()
  const isSubmenu = submenuProps?.submenuLevel !== undefined
  const defaultVariant = isSubmenu ? 'nested' : 'regular'
  const displayVariant = INTERNAL_variant ?? defaultVariant
  const htmlId = useId()

  const listBase = (internalVariant?: 'regular' | 'nested', labelBy?: string) => {
    switch (props.variant) {
      case 'multi':
        return (
          <SelectItemDataProvider value={listData.itemData}>
            {hasSearch ? (
              <SelectListBase
                keyboardDelegate={keyboardDelegate}
                variant={internalVariant ?? displayVariant}
                state={state}
                onSearchChange={onSearchChange}
                onFocusItem={onFocusSelectListItem}
                search={search}
                aria-label={ariaLabel}
                aria-labelledby={labelBy ?? ariaLabelledBy}
                keepMaxWidth={INTERNAL_keepMaxWidth}
                isLoading={isLoading}
              />
            ) : (
              <SelectListBase
                keyboardDelegate={keyboardDelegate}
                variant={internalVariant ?? displayVariant}
                state={state}
                aria-label={ariaLabel}
                aria-labelledby={labelBy ?? ariaLabelledBy}
                keepMaxWidth={INTERNAL_keepMaxWidth}
                onFocusItem={onFocusSelectListItem}
                isLoading={isLoading}
              />
            )}
          </SelectItemDataProvider>
        )
      default:
        return (
          <StyledWrapperFlexColumn variant={internalVariant ?? displayVariant}>
            <BaseSelectCascade
              aria-label={ariaLabel}
              aria-labelledby={labelBy ?? ariaLabelledBy}
              state={state}
              listStateData={listData}
              keyboardDelegate={keyboardDelegate}
              useVirtualScroll={useVirtualScroll}
              hasSearch={hasSearch}
              search={search}
              onChange={onChangeHandler}
              onSearchChange={onSearchChange}
              setSelectedCascadeItem={setSelectedCascadeItem}
              selectedCascadeItem={selectedCascadeItem}
              isLoading={isLoading}
              value={props.value}
              triggerWidth={triggerWidth}
            />
          </StyledWrapperFlexColumn>
        )
    }
  }

  if (label) {
    invariant(
      typeValidator(SelectListLabel)(label),
      `SelectList label must be an SelectList.Label component`
    )
    if (label.props.position === 'top') {
      return (
        <StyledLabelBoxContainer
          borderRadius={12}
          bg='bg/moderate'
          boxShadow='border/on-major'
          overflow='hidden'
        >
          <SettingLabelContainer
            isRequired={false}
            label={label}
            labelElement='label'
            labelProps={{ id: htmlId }}
          >
            {listBase('nested', htmlId)}
          </SettingLabelContainer>
        </StyledLabelBoxContainer>
      )
    }
    return (
      <SettingLabelContainer
        isRequired={false}
        label={label}
        labelElement='label'
        labelProps={{ id: htmlId }}
      >
        {listBase(undefined, htmlId)}
      </SettingLabelContainer>
    )
  }
  return listBase()
}

export const SelectList = (props: Props) => {
  return (
    <CascadeListFocusProvider>
      <SelectListInternal {...props} />
    </CascadeListFocusProvider>
  )
}

const SelectListLabel = (props: React.ComponentProps<typeof SettingLabel>) => (
  <SettingLabel {...props} />
)

const StyledLabelBoxContainer = styled(Box)`
  display: flex;

  > div:first-child {
    display: flex;
    flex-direction: column;

    > div:first-child {
      padding: 0;

      label {
        box-shadow: ${({ theme }) => theme.shadows['border/list-item']};
        padding: ${({ theme }) => `22px ${theme.space[5]} ${theme.space[3]} ${theme.space[5]}`};

        span {
          color: ${({ theme }) => theme.colors['fg/moderate']};
          font-size: ${({ theme }) => theme.fontSizes['14']};
          font-weight: ${({ theme }) => theme.fontWeights['600']};
          line-height: 100%;
        }
      }
    }

    > div:last-child {
      display: flex;
    }
  }
`

const StyledWrapperFlexColumn = styled(Flex)<{ variant: 'nested' | 'regular' }>`
  max-height: inherit;
  overflow: hidden;
  flex-direction: column;
  border-radius: ${({ theme, variant }) => (variant === 'regular' ? theme.radii['8'] : 'unset')};
  background-color: ${({ theme }) => theme.colors['bg/dropdown']};
  box-shadow: ${({ theme, variant }) =>
    variant === 'regular' ? theme.shadows['border/popover'] : undefined};
`

SelectList.Label = SelectListLabel
SelectList.Item = SelectItem
SelectList.StartContent = SelectStartContent
SelectList.EndContent = SelectEndContent
SelectList.Section = SelectSection
SelectList.ItemPlaceholder = SelectItemPlaceholder
SelectList.CascadeItem = CascadeSelectItem
SelectList.CascadeItems = CascadeItems
